/**
 * Workspace Service
 * 
 * Manages workspace (tenant) operations including:
 * - Workspace CRUD
 * - Membership management
 * - Demo workspace creation
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabaseClient';
import { defaultWorkspaceFeatures } from './workspaceModulesService';
// getOrCreateSupabaseUser removed — Clerk is disabled; callers pass Supabase UUIDs directly.

// ============================================================================
// Types
// ============================================================================

export type WorkspacePlan = 'free' | 'starter' | 'professional' | 'enterprise';
export type WorkspaceStatus = 'active' | 'suspended' | 'archived' | 'demo';
export type MemberRole = 'owner' | 'admin' | 'member' | 'viewer' | 'guest';
export type MemberStatus = 'active' | 'invited' | 'suspended';

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logoUrl?: string;
  plan: WorkspacePlan;
  planExpiresAt?: string;
  features: Record<string, boolean>;
  settings: WorkspaceSettings;
  status: WorkspaceStatus;
  isDemo: boolean;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
}

export interface WorkspaceSettings {
  defaultTimeHorizon: string;
  requireApprovalForPublish: boolean;
  allowGuestSubmissions: boolean;
  maxActiveProcesses: number;
  demoExpiresAt?: string;
}

export interface WorkspaceMembership {
  id: string;
  userId: string;
  workspaceId: string;
  role: MemberRole;
  status: MemberStatus;
  invitedBy?: string;
  invitedAt?: string;
  acceptedAt?: string;
  createdAt: string;
}

export interface CreateWorkspaceInput {
  name: string;
  slug?: string;
  description?: string;
  plan?: WorkspacePlan;
  isDemo?: boolean;
  tenantId?: string;
}

// ============================================================================
// Workspace Operations
// ============================================================================

/**
 * List workspaces the current user has access to
 */
export async function listUserWorkspaces(clerkUserId: string): Promise<Workspace[]> {
  const supabase = getSupabaseClient();
  const userId = clerkUserId;
  
  const { data, error } = await supabase
    .from('workspaces')
    .select(`
      *,
      workspace_memberships!inner(user_id, role)
    `)
    .eq('workspace_memberships.user_id', userId)
    .order('name');
  
  if (error) {
    console.error('[WorkspaceService] Error listing workspaces:', error);
    throw error;
  }
  
  return (data || []).map(mapWorkspaceFromRow);
}

/**
 * Get a single workspace by ID
 */
export async function getWorkspace(workspaceId: string): Promise<Workspace | null> {
  const supabase = getSupabaseClient();
  
  const { data, error } = await supabase
    .from('workspaces')
    .select('*')
    .eq('id', workspaceId)
    .single();
  
  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }
  
  return mapWorkspaceFromRow(data);
}

/**
 * Get workspace by slug
 */
export async function getWorkspaceBySlug(slug: string): Promise<Workspace | null> {
  const supabase = getSupabaseClient();
  
  const { data, error } = await supabase
    .from('workspaces')
    .select('*')
    .eq('slug', slug)
    .single();
  
  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  
  return mapWorkspaceFromRow(data);
}

/**
 * Ensure the workspace has a canonical org chart root (depth 0, type organization).
 * The root may have queue_settings enabled so intake has a default landing queue
 * (Workspace Initialization Invariant).
 *
 * Replaces the legacy pattern that created a separate `workspace-queue` node.
 * See DOCS_V4/00-canon/10P-workspace-organization-org-chart-and-membership.md
 */
export async function ensureWorkspaceOrgRoot(
  supabase: SupabaseClient,
  workspaceId: string,
  workspaceName?: string
): Promise<string> {
  const displayName = workspaceName || 'Organization';
  const defaultQueueSettings = {
    enabled: true,
    acceptsSubmissions: true,
    requiresApproval: false,
  };

  const { data: roots } = await supabase
    .from('org_units')
    .select('id, name, type, queue_settings')
    .eq('workspace_id', workspaceId)
    .eq('depth', 0)
    .eq('active', true);

  const activeRoots = roots ?? [];
  const nonLegacyRoots = activeRoots.filter((r) => r.name !== 'workspace-queue');
  const organizationRoot = nonLegacyRoots.find((r) => r.type === 'organization');

  // Canon 10P: only type=organization counts as the workspace root — not orphan departments.
  if (organizationRoot) {
    const queueEnabled = Boolean(
      (organizationRoot.queue_settings as { enabled?: boolean } | null)?.enabled,
    );
    if (!queueEnabled) {
      await supabase
        .from('org_units')
        .update({
          queue_settings: {
            ...defaultQueueSettings,
            ...((organizationRoot.queue_settings as Record<string, unknown> | null) ?? {}),
            enabled: true,
          },
          updated_at: new Date().toISOString(),
        })
        .eq('id', organizationRoot.id);
    }
    return organizationRoot.id;
  }

  const legacyQueue = activeRoots.find((r) => r.name === 'workspace-queue');
  if (legacyQueue) {
    await supabase
      .from('org_units')
      .update({
        name: 'organization',
        type: 'organization',
        display_name: displayName,
        queue_settings: {
          ...defaultQueueSettings,
          ...((legacyQueue.queue_settings as Record<string, unknown> | null) ?? {}),
          enabled: true,
        },
        updated_at: new Date().toISOString(),
      })
      .eq('id', legacyQueue.id);
    return legacyQueue.id;
  }

  const id = crypto.randomUUID();
  const now = new Date().toISOString();
  const { error } = await supabase.from('org_units').insert({
    id,
    name: 'organization',
    display_name: displayName,
    type: 'organization',
    parent_id: null,
    path: [id],
    depth: 0,
    active: true,
    workspace_id: workspaceId,
    queue_settings: defaultQueueSettings,
    created_at: now,
    updated_at: now,
  });

  if (error) {
    console.error('[WorkspaceService] Failed to create workspace org root:', workspaceId, error);
    throw error;
  }

  return id;
}

/**
 * Ensure canonical org root exists and orphan chart nodes are parented under it.
 * Canon 10P acceptance: single-root org chart for cycle scope pickers.
 */
export async function ensureWorkspaceOrgChartIntegrity(
  supabase: SupabaseClient,
  workspaceId: string,
  workspaceName?: string,
): Promise<string> {
  const rootId = await ensureWorkspaceOrgRoot(supabase, workspaceId, workspaceName);

  const { data: units, error: fetchError } = await supabase
    .from('org_units')
    .select('id, type, parent_id')
    .eq('workspace_id', workspaceId)
    .eq('active', true);

  if (fetchError) {
    console.warn('[WorkspaceService] ensureWorkspaceOrgChartIntegrity fetch failed:', fetchError.message);
    return rootId;
  }

  const rows = units ?? [];
  const idSet = new Set(rows.map((u) => u.id as string));
  const orphans = rows.filter((u) => {
    if ((u.id as string) === rootId) return false;
    if (u.type === 'organization') return false;
    const parentId = u.parent_id as string | null;
    return !parentId || !idSet.has(parentId);
  });

  if (orphans.length === 0) return rootId;

  const now = new Date().toISOString();
  await Promise.all(
    orphans.map((orphan) =>
      supabase
        .from('org_units')
        .update({
          parent_id: rootId,
          path: [rootId, orphan.id as string],
          depth: 1,
          updated_at: now,
        })
        .eq('id', orphan.id as string),
    ),
  );

  return rootId;
}

/** @deprecated Use ensureWorkspaceOrgRoot */
export async function ensureRootQueueForWorkspace(
  supabase: SupabaseClient,
  workspaceId: string,
  workspaceName?: string
): Promise<void> {
  await ensureWorkspaceOrgRoot(supabase, workspaceId, workspaceName);
}

/**
 * Ensure every org_unit in a workspace has a corresponding default group.
 * Groups are linked via `org_unit_id` and marked `is_system = true`.
 */
export async function ensureDefaultGroupsForOrgUnits(
  supabase: SupabaseClient,
  workspaceId: string
): Promise<void> {
  const { data: orgUnits } = await supabase
    .from('org_units')
    .select('id, display_name, parent_id, depth')
    .eq('workspace_id', workspaceId)
    .eq('active', true);

  if (!orgUnits || orgUnits.length === 0) return;

  const { data: existingGroups } = await supabase
    .from('groups')
    .select('id, org_unit_id')
    .eq('workspace_id', workspaceId)
    .eq('is_system', true);

  const coveredOrgUnitIds = new Set(
    (existingGroups || []).map((g: { org_unit_id: string | null }) => g.org_unit_id).filter(Boolean)
  );

  // Build a map of org_unit_id → group_id for resolving parent groups
  const orgUnitToGroupId = new Map<string, string>();
  for (const g of existingGroups || []) {
    if (g.org_unit_id) orgUnitToGroupId.set(g.org_unit_id, g.id);
  }

  // Sort by depth so parents are created before children
  const sorted = [...orgUnits].sort(
    (a: { depth: number }, b: { depth: number }) => a.depth - b.depth
  );

  for (const unit of sorted) {
    if (coveredOrgUnitIds.has(unit.id)) continue;

    const groupId = crypto.randomUUID();
    const parentGroupId = unit.parent_id ? orgUnitToGroupId.get(unit.parent_id) ?? null : null;
    const now = new Date().toISOString();

    const { error } = await supabase.from('groups').insert({
      id: groupId,
      name: unit.display_name.toLowerCase().replace(/\s+/g, '-'),
      display_name: unit.display_name,
      description: `Default group for ${unit.display_name}`,
      org_unit_id: unit.id,
      parent_id: parentGroupId,
      is_system: true,
      workspace_id: workspaceId,
      created_at: now,
      updated_at: now,
    });

    if (error) {
      console.error(`[WorkspaceService] Failed to create default group for org unit ${unit.display_name}:`, error);
    } else {
      orgUnitToGroupId.set(unit.id, groupId);
      coveredOrgUnitIds.add(unit.id);
    }
  }
}

/**
 * Create a new workspace
 */
export async function createWorkspace(
  input: CreateWorkspaceInput,
  clerkUserId: string
): Promise<Workspace> {
  const supabase = getSupabaseClient();
  const userId = clerkUserId;
  
  // Generate slug if not provided
  const slug = input.slug || generateSlug(input.name);
  
  const { data, error } = await supabase
    .from('workspaces')
    .insert({
      name: input.name,
      slug,
      description: input.description,
      plan: input.plan || 'free',
      is_demo: input.isDemo || false,
      status: input.isDemo ? 'demo' : 'active',
      owner_id: userId,
      ...(input.tenantId ? { tenant_id: input.tenantId } : {}),
      // Explicit registry-default module map — the DB gate reads raw keys
      // (missing = off) while the client falls back to registry defaults, so
      // an unseeded workspace shows modules ON that RLS denies (mig 691).
      features: defaultWorkspaceFeatures(),
      settings: {
        defaultTimeHorizon: 'quarter',
        requireApprovalForPublish: false,
        allowGuestSubmissions: true,
        maxActiveProcesses: input.plan === 'enterprise' ? 50 : input.plan === 'professional' ? 10 : 5,
      },
    })
    .select()
    .single();
  
  if (error) throw error;
  
  // Add owner as member
  await supabase
    .from('workspace_memberships')
    .insert({
      user_id: userId,
      workspace_id: data.id,
      role: 'owner',
      status: 'active',
      accepted_at: new Date().toISOString(),
    });
  
  // Workspace Initialization Invariant: canonical org root with queue enabled
  await ensureWorkspaceOrgRoot(supabase, data.id, input.name);

  // Create default groups mirroring the org hierarchy
  await ensureDefaultGroupsForOrgUnits(supabase, data.id);

  return mapWorkspaceFromRow(data);
}

/**
 * Create a demo workspace with sample data
 */
export async function createDemoWorkspace(clerkUserId: string): Promise<Workspace> {
  const supabase = getSupabaseClient();
  const userId = clerkUserId;
  
  // Use the database function to create demo workspace
  const { data, error } = await supabase
    .rpc('create_demo_workspace', {
      owner_user_id: userId,
      demo_name: 'Demo Workspace',
    });
  
  if (error) {
    // Fallback to manual creation if function doesn't exist
    console.warn('[WorkspaceService] Demo function not found, creating manually');
    return createWorkspace({
      name: 'Demo Workspace',
      slug: `demo-${Date.now()}`,
      description: 'Demo workspace for exploring Peak Priorities features',
      plan: 'professional',
      isDemo: true,
    }, clerkUserId);
  }
  
  // Fetch the created workspace
  const workspace = await getWorkspace(data);
  if (!workspace) throw new Error('Failed to create demo workspace');

  // Workspace Initialization Invariant: canonical org root (RPC may not create org_units)
  await ensureWorkspaceOrgRoot(supabase, workspace.id, workspace.name);

  // Create default groups mirroring the org hierarchy
  await ensureDefaultGroupsForOrgUnits(supabase, workspace.id);

  // Seed demo data
  await seedDemoData(workspace.id, userId);

  return workspace;
}

/**
 * Update workspace settings
 */
export async function updateWorkspace(
  workspaceId: string,
  updates: Partial<Pick<Workspace, 'name' | 'description' | 'settings'>>
): Promise<Workspace> {
  const supabase = getSupabaseClient();
  
  const { data, error } = await supabase
    .from('workspaces')
    .update({
      name: updates.name,
      description: updates.description,
      settings: updates.settings,
      updated_at: new Date().toISOString(),
    })
    .eq('id', workspaceId)
    .select()
    .single();
  
  if (error) throw error;
  return mapWorkspaceFromRow(data);
}

// ============================================================================
// Membership Operations
// ============================================================================

/**
 * List members of a workspace
 */
export async function listWorkspaceMembers(workspaceId: string): Promise<(WorkspaceMembership & { user: { email: string; name: string } })[]> {
  const supabase = getSupabaseClient();

  // user_id in workspace_memberships is TEXT (not a UUID FK), so PostgREST's
  // implicit join doesn't work. Fetch memberships first, then look up users.
  const { data: memberships, error: memErr } = await supabase
    .from('workspace_memberships')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at');

  if (memErr) throw memErr;
  if (!memberships || memberships.length === 0) return [];

  const userIds = [...new Set(memberships.map((m) => m.user_id as string))];
  const { data: users, error: usersErr } = await supabase
    .from('users')
    .select('id, email, name')
    .in('id', userIds);

  if (usersErr) throw usersErr;

  const userMap = new Map((users ?? []).map((u: { id: string; email: string; name: string }) => [u.id, u]));

  return memberships.map((row) => {
    const u = userMap.get(row.user_id as string);
    return {
      id: row.id,
      userId: row.user_id,
      workspaceId: row.workspace_id,
      role: row.role,
      status: row.status,
      invitedBy: row.invited_by,
      invitedAt: row.invited_at,
      acceptedAt: row.accepted_at,
      createdAt: row.created_at,
      user: {
        email: u?.email || '',
        name: u?.name || '',
      },
    };
  });
}

/**
 * Get current user's membership for a workspace
 */
export async function getUserMembership(
  workspaceId: string,
  clerkUserId: string
): Promise<WorkspaceMembership | null> {
  const supabase = getSupabaseClient();
  const userId = clerkUserId;
  
  const { data, error } = await supabase
    .from('workspace_memberships')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)
    .single();
  
  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  
  return mapMembershipFromRow(data);
}

/**
 * Update a member's role
 */
export async function updateMemberRole(
  membershipId: string,
  newRole: MemberRole
): Promise<void> {
  const supabase = getSupabaseClient();
  
  const { error } = await supabase
    .from('workspace_memberships')
    .update({ role: newRole, updated_at: new Date().toISOString() })
    .eq('id', membershipId);
  
  if (error) throw error;
}

/**
 * Remove a member from workspace
 */
export async function removeMember(membershipId: string): Promise<void> {
  const supabase = getSupabaseClient();
  
  const { error } = await supabase
    .from('workspace_memberships')
    .delete()
    .eq('id', membershipId);
  
  if (error) throw error;
}

// ============================================================================
// Helper Functions
// ============================================================================

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50);
}

function mapWorkspaceFromRow(row: Record<string, unknown>): Workspace {
  return {
    id: row.id as string,
    name: row.name as string,
    slug: row.slug as string,
    description: (row.description as string | null) ?? undefined,
    logoUrl: (row.logo_url as string | null) ?? undefined,
    plan: row.plan as Workspace['plan'],
    planExpiresAt: (row.plan_expires_at as string | null) ?? undefined,
    features: (row.features as Workspace['features']) || {},
    settings: (row.settings as Workspace['settings']) || {},
    status: row.status as Workspace['status'],
    isDemo: row.is_demo as boolean,
    ownerId: row.owner_id as string,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function mapMembershipFromRow(row: Record<string, unknown>): WorkspaceMembership {
  return {
    id: row.id as string,
    userId: row.user_id as string,
    workspaceId: row.workspace_id as string,
    role: row.role as WorkspaceMembership['role'],
    status: row.status as WorkspaceMembership['status'],
    invitedBy: (row.invited_by as string | null) ?? undefined,
    invitedAt: (row.invited_at as string | null) ?? undefined,
    acceptedAt: (row.accepted_at as string | null) ?? undefined,
    createdAt: row.created_at as string,
  };
}

/**
 * Seed comprehensive demo data into a workspace
 * Matches DEMO_SCRIPT.md requirements for Acme Corp scenario
 */
async function seedDemoData(workspaceId: string, userId: string): Promise<void> {
  const supabase = getSupabaseClient();
  
  const demoCandidates = [
    { title: 'Mobile App Push Notifications', description: 'Allow users to receive push notifications for important updates.', lifecycle_state: 'item', exit_status: 'active', altitude: 'feature' },
    { title: 'Dark Mode Support', description: 'Implement a dark mode theme option for the application to reduce eye strain.', lifecycle_state: 'item', exit_status: 'active', altitude: 'feature' },
    { title: 'Bulk Export to CSV', description: 'Enable users to export their data in CSV format for external analysis.', lifecycle_state: 'item', exit_status: 'active', altitude: 'feature' },
    
    // ITEMS (8)
    { title: 'Real-time Collaboration', description: 'Enable multiple users to work on the same document simultaneously with live cursors and presence indicators.', lifecycle_state: 'item', exit_status: 'active', altitude: 'initiative' },
    { title: 'Advanced Analytics Dashboard', description: 'Build a comprehensive analytics dashboard showing usage patterns and engagement metrics.', lifecycle_state: 'item', exit_status: 'active', altitude: 'initiative' },
    { title: 'Single Sign-On (SSO) Integration', description: 'Support enterprise SSO providers including Okta, Azure AD, and Google Workspace.', lifecycle_state: 'item', exit_status: 'active', altitude: 'feature' },
    { title: 'API Rate Limiting & Throttling', description: 'Implement intelligent rate limiting to protect the API from abuse.', lifecycle_state: 'item', exit_status: 'active', altitude: 'feature' },
    { title: 'Custom Workflow Automation', description: 'Allow users to create custom automation rules triggered by events.', lifecycle_state: 'item', exit_status: 'active', altitude: 'initiative' },
    { title: 'Audit Log & Compliance Reporting', description: 'Comprehensive audit trail with exportable compliance reports for SOC2 and GDPR.', lifecycle_state: 'item', exit_status: 'active', altitude: 'feature' },
    { title: 'Multi-language Support (i18n)', description: 'Localize the application into Spanish, French, German, Japanese, and Portuguese.', lifecycle_state: 'item', exit_status: 'active', altitude: 'feature' },
    { title: 'Offline Mode with Sync', description: 'Enable users to work offline with automatic synchronization when connectivity is restored.', lifecycle_state: 'item', exit_status: 'active', altitude: 'initiative' },
    
    // PRIORITIES (4)
    { title: 'Two-Factor Authentication (2FA)', description: 'Add optional 2FA using authenticator apps or SMS for enhanced account security.', lifecycle_state: 'item', exit_status: 'active', altitude: 'feature' },
    { title: 'Team Workspaces', description: 'Organizational workspaces allowing teams to share resources and collaborate effectively.', lifecycle_state: 'item', exit_status: 'active', altitude: 'initiative' },
    { title: 'Webhook Integrations', description: 'Send real-time event notifications to external systems via configurable webhooks.', lifecycle_state: 'item', exit_status: 'active', altitude: 'feature' },
    { title: 'Performance Optimization Sprint', description: 'Reduce page load times by 50% through code splitting, caching, and database optimization.', lifecycle_state: 'item', exit_status: 'active', altitude: 'initiative' },
  ];
  
  const candidateResult = await supabase
    .from('items')
    .insert(demoCandidates.map(c => ({
      ...c,
      workspace_id: workspaceId,
      created_by: userId,
    })))
    .select('id, title');
  
  const candidateMap: Record<string, string> = {};
  (candidateResult.data || []).forEach(c => { candidateMap[c.title] = c.id; });
  
  // ============================================================================
  // IDEA SUBMISSIONS (5)
  // ============================================================================
  const submissions = [
    { title: 'AI-Powered Search', description: 'Implement semantic search using AI to help users find content more naturally.', submitter_name: 'Jordan Kim', status: 'INTAKE_PENDING', category: 'Feature', priority: 'high' },
    { title: 'Calendar Integration', description: 'Sync with Google Calendar and Outlook to schedule tasks. Users have requested this frequently.', submitter_name: 'Sam Nguyen', status: 'INTAKE_PENDING', category: 'Integration', priority: 'medium' },
    { title: 'Custom Branding Options', description: 'Allow enterprise customers to customize colors, logos, and email templates.', submitter_name: 'Riley Thompson', status: 'ACTIVE', category: 'Enterprise', priority: 'high' },
    { title: 'Keyboard Shortcuts', description: 'Add comprehensive keyboard shortcuts for power users to navigate faster.', submitter_name: 'Taylor Patel', status: 'INTAKE_PENDING', category: 'UX', priority: 'low' },
    { title: 'Data Backup & Recovery', description: 'Automatic backups with point-in-time recovery options for enterprise accounts.', submitter_name: 'Morgan Davis', status: 'READY', category: 'Infrastructure', priority: 'critical' },
  ];
  
  await supabase.from('idea_submissions').insert(submissions.map(s => ({
    ...s,
    workspace_id: workspaceId,
    submitted_by: s.submitter_name,
    submitter_id: userId,
  })));
  
  // ============================================================================
  // FOCUS LISTS (4)
  // ============================================================================
  const focusLists = [
    { name: 'Q1 2026 Product Roadmap', description: 'Primary focus areas for Q1 2026 including security, performance, and collaboration.', status: 'active' },
    { name: 'Security & Compliance Initiatives', description: 'Features required for SOC2 Type II certification.', status: 'active' },
    { name: 'Customer-Requested Features', description: 'High-impact features requested by multiple enterprise customers.', status: 'active' },
    { name: 'Technical Debt Reduction', description: 'Infrastructure improvements and code quality initiatives.', status: 'draft' },
  ];
  
  await supabase.from('item_pools').insert(focusLists.map(f => ({
    ...f,
    workspace_id: workspaceId,
    owner_id: userId,
  })));
  
  // ============================================================================
  // DEGAP CYCLES (3 in different phases)
  // ============================================================================
  const cycles = [
    {
      name: 'Q1 2026 Planning Cycle',
      description: 'Complete DEGAP cycle for Q1 2026 product planning.',
      status: 'active',
      current_phase: 'ARRANGE',
      outputs: {
        decide: {
          outcomeDrivers: [
            { id: 'od1', statement: 'Increase enterprise adoption by 30%', rationale: 'Key growth metric' },
            { id: 'od2', statement: 'Achieve SOC2 Type II certification', rationale: 'Required for enterprise deals' },
            { id: 'od3', statement: 'Reduce customer churn by 15%', rationale: 'Retention is critical' },
          ],
        },
        engage: { stakeholdersFeedback: [] },
        gather: { candidateCount: 15, sourcesUsed: ['customer_feedback', 'support_tickets', 'sales_requests'] },
        arrange: {},
        prioritize: {},
      },
    },
    {
      name: 'Security Hardening Initiative',
      description: 'Focused cycle on security improvements.',
      status: 'active',
      current_phase: 'ENGAGE',
      outputs: { decide: { outcomeDrivers: [{ id: 'od1', statement: 'Pass security audit with zero critical findings' }] }, engage: {}, gather: {}, arrange: {}, prioritize: {} },
    },
    {
      name: 'Customer Feature Sprint',
      description: 'Quick cycle to prioritize customer-requested features.',
      status: 'active',
      current_phase: 'DECIDE',
      outputs: { decide: { outcomeDrivers: [] }, engage: {}, gather: {}, arrange: {}, prioritize: {} },
    },
  ];
  
  await supabase.from('degap_cycles').insert(cycles.map(c => ({
    ...c,
    workspace_id: workspaceId,
    created_by: userId,
    started_at: new Date().toISOString(),
  })));
  
  // ============================================================================
  // CLUSTERS (3)
  // ============================================================================
  const clusters = [
    { title: 'Authentication & Security', description: 'Features related to user authentication and security.' },
    { title: 'Collaboration Features', description: 'Real-time collaboration, sharing, and team features.' },
    { title: 'Enterprise Capabilities', description: 'Features required for enterprise customers including SSO, audit logs, and compliance.' },
  ];
  
  await supabase.from('item_clusters').insert(clusters.map(c => ({
    ...c,
    workspace_id: workspaceId,
    created_by: userId,
  })));
  
  console.log('[WorkspaceService] Comprehensive demo data seeded for workspace:', workspaceId);
  console.log('  - 10 demo users');
  console.log('  - 15 items (3 intake, 8 shaping/ready, 4 in pools)');
  console.log('  - 5 idea submissions');
  console.log('  - 4 Item Pools');
  console.log('  - 3 DEGAP cycles');
  console.log('  - 3 clusters');
}
